// ErrorBoundary dedicado ao PWA do funcionário.
// Em iOS Safari standalone, uma exceção não capturada na render tree deixa a tela
// 100% branca (não há overlay de dev). Este boundary garante que o funcionário
// SEMPRE veja uma tela útil + consiga resetar o app sem reinstalar.
//
// Também registra o erro em localStorage ('dp_last_error') para diagnóstico futuro.

import React from "react";

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: "", errorStack: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMsg: String(error?.message || error || "Erro desconhecido"),
      errorStack: String(error?.stack || "").slice(0, 500),
    };
  }

  componentDidCatch(error, info) {
    try {
      const payload = {
        ts: new Date().toISOString(),
        ua: (navigator.userAgent || "").slice(0, 300),
        msg: String(error?.message || error),
        stack: String(error?.stack || "").slice(0, 1500),
        componentStack: String(info?.componentStack || "").slice(0, 1500),
        url: window.location.href,
      };
      localStorage.setItem("dp_last_error", JSON.stringify(payload));
    } catch (_) { /* storage pode estar bloqueado em modo privado */ }
    // Garante que a splash some mesmo se o erro tiver ocorrido antes do fade
    try {
      const splash = document.getElementById("digiponto-splash");
      if (splash) splash.remove();
    } catch (_) {}
  }

  resetApp = async () => {
    try {
      // 1. Unregister de todos os service workers
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      // 2. Limpa todos os caches
      if (window.caches) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
      }
      // 3. Limpa storages (mantém device_id pra não quebrar binding do funcionário)
      try {
        const deviceId = localStorage.getItem("dp_device_id");
        localStorage.clear();
        if (deviceId) localStorage.setItem("dp_device_id", deviceId);
        sessionStorage.clear();
      } catch (_) {}
    } finally {
      // Força reload sem cache
      window.location.replace("/app/login?reset=" + Date.now());
    }
  };

  copyError = async () => {
    const text = `DigiPonto erro:\n${this.state.errorMsg}\n\n${this.state.errorStack}\n\nUA: ${navigator.userAgent}`;
    try {
      await navigator.clipboard.writeText(text);
      alert("Erro copiado — envie pro suporte");
    } catch (_) {
      // fallback
      window.prompt("Copie o erro abaixo e envie pro suporte:", text);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          background: "#0f172a",
          color: "#f1f5f9",
          padding: "24px 20px",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 16,
            padding: 20,
            boxShadow: "0 10px 40px rgba(0,0,0,.4)",
          }}
        >
          <div style={{ fontSize: 44, textAlign: "center", lineHeight: 1 }}>⚠️</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, textAlign: "center", margin: "12px 0 4px" }}>
            Opa, o app teve um problema
          </h1>
          <p style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", margin: 0 }}>
            Isso é raro — toque em "Resetar app" para continuar.
          </p>

          <div
            style={{
              marginTop: 16,
              background: "#0f172a",
              border: "1px solid #334155",
              borderRadius: 8,
              padding: 10,
              fontFamily: "ui-monospace, Menlo, monospace",
              fontSize: 11,
              color: "#fca5a5",
              maxHeight: 110,
              overflow: "auto",
            }}
          >
            {this.state.errorMsg}
          </div>

          <button
            data-testid="btn-reset-app"
            onClick={this.resetApp}
            style={{
              marginTop: 16,
              width: "100%",
              height: 52,
              borderRadius: 12,
              border: "none",
              background: "linear-gradient(135deg,#059669,#047857)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 6px 18px rgba(5,150,105,.35)",
            }}
          >
            Resetar app
          </button>

          <button
            data-testid="btn-copy-error"
            onClick={this.copyError}
            style={{
              marginTop: 8,
              width: "100%",
              height: 40,
              borderRadius: 10,
              border: "1px solid #475569",
              background: "transparent",
              color: "#cbd5e1",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Copiar erro p/ suporte
          </button>

          <p style={{ fontSize: 11, color: "#64748b", textAlign: "center", marginTop: 16 }}>
            Resetar limpa cache do app, mantém seu login vinculado ao celular.
          </p>
        </div>
      </div>
    );
  }
}
